import React, { useCallback, useEffect, useState } from 'react'
import { useGetSoftLaunch } from '../../../components/SoftLaunch'
import { SoftLaunch } from '../../../components/SoftLaunch'
import {
  defaultSoftLaunchSamplesNeeded as computeDefaultSoftLaunchSamplesNeeded,
  isSoftLaunchSamplesValid,
  softLaunchSamplesErrorMessage
} from '../../../components/SoftLaunch'
import { useUpdateFieldworkV2 } from '../../../hooks/audience/createOrUpdateFieldworkV2'
import { useMarketLanguages } from '../../../hooks/audience/useMarketLanguages'
import { useFieldwork } from '../../../hooks/useFieldwork'
import { useSurveyId } from '../../../hooks/useSurveyId'
import {
  PanelSupplier,
  convertAudienceType
} from '../../../utils/audienceHelper'
import ConfirmationSoftLaunchControl from './ConfirmationSoftLaunch.control'

interface Props {
  toggleOn?: boolean
  startNumOfCompletes?: number
  minNumOfCompletes?: number
}

const ConfirmationSoftLaunchContainer: React.FC<Props> = (props: Props) => {
  const surveyId = useSurveyId()
  const {
    toggleOn = false,
    startNumOfCompletes = 100,
    minNumOfCompletes = 1
  }: Props = props

  const firstFetchPolicy = 'no-cache'
  const {
    isConfiguredForSoftLaunch,
    softLaunchSamplesNeeded,
    samplesNeeded: fieldworkSamplesNeeded
  } = useGetSoftLaunch(firstFetchPolicy)

  const { fieldwork } = useFieldwork()
  // Multi-language markets route through a multi-locale launch path that
  // ignores soft launch, so the option must not be offered.
  const { isMultiLanguage } = useMarketLanguages()

  const { updateFieldworkV2 } = useUpdateFieldworkV2()
  const minfieldworkSamplesNeeded = fieldworkSamplesNeeded * 0.1
  const isConfiguredForIncrementalFieldwork =
    fieldwork?.samplesNeededIncreaseFrequency !== null
  // Shared with the multi-market Confirm page (SoftLaunch.utils) so the
  // default size and bounds can't drift between the two surfaces.
  const defaultSoftLaunchSamplesNeeded = computeDefaultSoftLaunchSamplesNeeded(
    fieldworkSamplesNeeded,
    startNumOfCompletes
  )
  const minNumOfCompletesErrorMessage = softLaunchSamplesErrorMessage(
    fieldworkSamplesNeeded,
    minNumOfCompletes
  )
  const infoText =
    'Launch your project initially with a fraction of the sample. Check for any issues. Then launch fully and see all data together in one place.'
  const panelSupplierCode = convertAudienceType(
    fieldwork?.audience[0]?.__typename
  )

  const [isToggledOn, setIsToggledOn] = useState<boolean>(toggleOn)
  const [softLaunchNumOfCompletes, setSoftLaunchNumOfCompletes] =
    useState<number>(defaultSoftLaunchSamplesNeeded)
  const [hasInputError, setHasInputError] = useState<boolean>(false)

  const handleUpdateFieldworkSoftLaunch: (value: any) => void = useCallback(
    (value) => {
      const softLaunch: SoftLaunch = {
        samplesNeeded: Number(value)
      }
      updateFieldworkV2({
        variables: {
          input: {
            surveyId,
            panelSupplierCode: panelSupplierCode,
            softLaunch
          }
        }
      })
    },
    [panelSupplierCode, surveyId, updateFieldworkV2]
  )
  const showSoftLaunchControls =
    panelSupplierCode === PanelSupplier.CINT ||
    panelSupplierCode === PanelSupplier.PURESPECTRUM

  const handleToggle: () => void = () => {
    if (isMultiLanguage) return
    const newIsToggleOn = !isToggledOn
    const softLaunchCompletes = newIsToggleOn
      ? defaultSoftLaunchSamplesNeeded
      : 0

    setIsToggledOn(newIsToggleOn)
    setSoftLaunchNumOfCompletes(softLaunchCompletes)
    handleUpdateFieldworkSoftLaunch(softLaunchCompletes)
  }

  const handleInputBlur: (value: string) => void = (value) => {
    const numValue = Number(value)

    if (
      isSoftLaunchSamplesValid(
        numValue,
        fieldworkSamplesNeeded,
        minNumOfCompletes
      )
    ) {
      setSoftLaunchNumOfCompletes(numValue)
      handleUpdateFieldworkSoftLaunch(value)
      setHasInputError(false)
    } else {
      setHasInputError(true)
    }
  }

  const toggleSoftLaunchOff: () => void = useCallback(() => {
    setIsToggledOn(false)
    setSoftLaunchNumOfCompletes(0)
    handleUpdateFieldworkSoftLaunch(0)
  }, [
    setIsToggledOn,
    setSoftLaunchNumOfCompletes,
    handleUpdateFieldworkSoftLaunch
  ])

  useEffect(() => {
    // set toggleOn state here
    setIsToggledOn(isConfiguredForSoftLaunch)

    if (isConfiguredForIncrementalFieldwork || isMultiLanguage) {
      setIsToggledOn(false)
      setSoftLaunchNumOfCompletes(0)
    }
    // set startNumOfCompletes state here
    else if (
      softLaunchSamplesNeeded >= minNumOfCompletes &&
      softLaunchSamplesNeeded <= fieldworkSamplesNeeded
    )
      setSoftLaunchNumOfCompletes(softLaunchSamplesNeeded)
    else if (softLaunchSamplesNeeded > fieldworkSamplesNeeded)
      toggleSoftLaunchOff()
    else if (minfieldworkSamplesNeeded > minNumOfCompletes)
      setSoftLaunchNumOfCompletes(minfieldworkSamplesNeeded)
    else if (startNumOfCompletes < fieldworkSamplesNeeded)
      setSoftLaunchNumOfCompletes(startNumOfCompletes)
    else setSoftLaunchNumOfCompletes(minNumOfCompletes)
  }, [
    isConfiguredForIncrementalFieldwork,
    isMultiLanguage,
    softLaunchSamplesNeeded,
    startNumOfCompletes,
    minfieldworkSamplesNeeded,
    fieldworkSamplesNeeded,
    minNumOfCompletes,
    isConfiguredForSoftLaunch,
    toggleSoftLaunchOff,
    surveyId
  ])

  // Use debounce for num input or on blur

  return (
    <ConfirmationSoftLaunchControl
      isToggledOn={isToggledOn}
      inputPlaceholder={startNumOfCompletes}
      softLaunchSamplesNeeded={softLaunchNumOfCompletes}
      onToggle={handleToggle}
      onInputBlur={handleInputBlur}
      hasInputError={hasInputError}
      inputErrorMessage={minNumOfCompletesErrorMessage}
      infoText={infoText}
      isConfiguredForIncrementalFieldwork={isConfiguredForIncrementalFieldwork}
      isMultiLanguage={isMultiLanguage}
      showSoftLaunchControls={showSoftLaunchControls}
    />
  )
}

export default ConfirmationSoftLaunchContainer
